import React, { useRef, useEffect, useState, useMemo, useCallback } from "react";
import { createPortal } from "react-dom";
import { useConfigurationComponentType } from "../../contexts/ConfigurationComponentType";
import Modal from "../../components/UI/Modal";
import ComponentTypeForm from "../../components/Form/ComponentTypeForm";
import ReusableTable from "../../components/UI/ReusableTable";
import DeleteButton from "../../components/UI/AlertDialog";
import Notification from "../../components/UI/Notification";
import { Description } from "@headlessui/react";
import Skeleton from "react-loading-skeleton";
import { X, Briefcase, MoreVertical, Building, Calendar, Users, MapPin, Award, Clock, Grid, Search } from 'lucide-react';
import DropdownMenu from "../../components/UI/DropdownMenu";
import StatusBadge from "../../components/UI/StatusBadge";

// Separate component for actions column to avoid hooks rule violation
const ActionsColumn = React.memo(({ row, openMenu, setOpenMenu, openModalForEdit, handleDeleteDepartment }) => {
  const buttonRef = useRef(null);
  
  return (
    <div className="flex items-center justify-center relative">
      <button
        ref={buttonRef}
        onClick={() =>
          setOpenMenu(openMenu === row.id ? null : row.id)
        }
        className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-full transition-colors"
      >
        <MoreVertical className="w-4 h-4 text-gray-600 dark:text-gray-400" />
      </button>

      {openMenu === row.id && (
        <DropdownMenu
          buttonRef={buttonRef}
          onClose={() => setOpenMenu(null)}
        >
          <button
            onClick={(e) => {
              openModalForEdit(row);
              setOpenMenu(null);
            }}
            className="w-full text-left px-3 py-2 text-sm text-blue-600 hover:bg-blue-50 dark:hover:bg-blue-900/20 transition-colors"
          >
            Edit
          </button>
          <DeleteButton
            style={`text-red-600 hover:text-red-800 px-3 py-2`}
            onDelete={(e) => {
              handleDeleteDepartment(row.id);
              setOpenMenu(null);
            }}
          />
        </DropdownMenu>
      )}
    </div>
  );
});

const SalaryComponentMaster = React.memo(() => {
  const { component_types, addComponentType, updateComponentType, deleteComponentType, pagination, isLoading } = useConfigurationComponentType();
  
  const [loading, setLoading] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingComponentType, setEditingComponentType] = useState(null);
  const [message, setMessage] = useState(""); // Success message
  const [formErrors, setFormErrors] = useState({}); // Validation errors
  const [notification, setNotification] = useState(null);
  const [openMenu, setOpenMenu] = useState(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [isClient, setIsClient] = useState(false);

  useEffect(() => {
    setIsClient(true); // Ensures document.body is available
  }, []);



  // Handlers
  const openModalForAdd = useCallback(() => {
    setEditingComponentType(null);
    setIsModalOpen(true);
    setMessage("");
    setFormErrors({});
  }, []);

  const openModalForEdit = useCallback((component_type) => {
    setEditingComponentType(component_type);
    setIsModalOpen(true);
    setMessage("");
    setFormErrors({});
  }, []);

  const closeModal = useCallback(() => {
    setIsModalOpen(false);
    setEditingComponentType(null);
    setFormErrors({});
    setMessage("");
  }, []);

  const convertFormDataToObject = (formData) => {
    return Object.fromEntries(formData.entries());
  };

  const handleFormSubmit = useCallback(
    async (component_type) => {
      setLoading(true);
      try {
        if (editingComponentType) {
          // console.log("component type payload", component type);
        
          // Convert FormData to object
          // console.log(department instanceof FormData)
          const componentTypeData = component_type instanceof FormData ? convertFormDataToObject(component_type) : component_type;
  
          // console.log("Payload being sent:", { ...editingComponentType, ...componentTypeData });
  
          await updateComponentType({ ...editingComponentType, ...componentTypeData });
          setMessage("Component Type updated successfully.");
          // setNotification("Component type updated successfully.");
          setNotification({
            message: "Component Type updated successfully.",
            type: "success",
          });
        } else {
          // console.log("create component type", component_type)
          await addComponentType(component_type);
          setMessage("Component type added successfully.");
          // setNotification("Component type added successfully.");
          setNotification({
            message: "Component type added successfully.",
            type: "success",
          });
        }
        closeModal();
      } catch (err) {
        const errors = err.response?.data?.errors || {};
        const apiMessage = err.response?.data?.message || err.response?.data?.error || "An error occurred";
        
        if (Object.keys(errors).length > 0) {
          setFormErrors(errors);
        } else {
          setNotification({
            message: apiMessage,
            type: "error",
          });
        }
      } finally {
        setLoading(false); 
      }
    },
    [editingComponentType, addComponentType, updateComponentType, closeModal]
  );

  // Memoize campaigns for dropdown options
//   const departmentOptions = useMemo(
//     () => departments.map((c) => ({ value: c.id, label: c.name })),
//     [departments]
//   );

  // Memoize candidates with campaign names for the table
  const componentTypeForTable = useMemo(
    () =>
      component_types.map((component_type) => ({
        ...component_type,
        name: component_type?.name || "Unknown Component type",
      })),
    [component_types]
  );

  const filteredComponentTypes = useMemo(() => {
    return componentTypeForTable.filter((item) => {
      const searchTerm = searchQuery.toLowerCase();
      return item.name.toLowerCase().includes(searchTerm);
    });
  }, [componentTypeForTable, searchQuery]);

  const handleDeleteComponentType = async (id) => {
    try {
      await deleteComponentType(id);
      // setNotification("Component type deleted successfully");
      setNotification({
        message: "Component type deleted successfully",
        type: "success",
      });
    } catch (error) {
      // setNotification("Failed to delete component type");
      setNotification({
        message: "Failed to delete component type",
        type: "error",
      });
    }
  };

  // Table columns
  const columns = useMemo(
    () => [
      { label: "Name", field: "name" },
      { label: "Code", field: "code" },
      { label: "Category", field: "category" },
      { label: "Display Order", field: "display_order" },
      { 
        label: "Status", 
        field: "is_active",
        render: (row) => <StatusBadge status={row.is_active} />
      },
      {
        label: "Actions",
        field: "actions",
        render: (row) => (
          <ActionsColumn
            row={row}
            openMenu={openMenu}
            setOpenMenu={setOpenMenu}
            openModalForEdit={openModalForEdit}
            handleDeleteDepartment={handleDeleteComponentType}
          />
        ),
      },
    ],
    [
      openMenu,
      openModalForEdit,
      handleDeleteComponentType,
    ]
  );

  return (
    <div className="px-3 py-2 bg-white dark:bg-gray-900 font-poppins rounded-lg">
      
      {/* notification compo */}
      {notification && (
        <Notification message={notification.message} type={notification.type} onClose={() => setNotification(null)} />
      )}

      <div className="mb-6">
        <h2 className="text-2xl font-semibold text-black dark:text-white">Component Type Masters</h2>
      </div>

      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
        {/* Left End: Search Bar */}
        <div className="relative w-full sm:max-w-xl"> 
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
          <input
            type="text"
            placeholder="Search by name..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full pl-10 pr-10 py-2 border border-gray-200 dark:border-gray-700 dark:bg-gray-800 dark:text-white rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 transition-all font-poppins"
          />
          {searchQuery && (
            <button
              onClick={() => setSearchQuery("")}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 transition-colors"
            >
              <X className="w-4 h-4" />
            </button>
          )}
        </div>

        {/* Right End: Add Button */}
        <button
          onClick={openModalForAdd}
          className="bg-blue-600 font-medium text-white px-4 py-2 rounded-md hover:bg-blue-700 whitespace-nowrap"
        >
          Add Component Type
        </button>
      </div>

      {/* Success Message */}
      {message && (
        <div className="mb-4 p-4 bg-green-100 border border-green-500 text-green-700 rounded-md">
          {message}
        </div>
      )}

      {/* Skeleton Loader */}
      {loading ? (
        <div className="space-y-4">
          {Array.from({ length: 5 }).map((_, index) => (
            <Skeleton key={index} height={40} />
          ))}
        </div>
      ) : (
        <div>
          <ReusableTable
            data={filteredComponentTypes}
            // data={requests.slice().reverse()}
            loading={isLoading}
            columns={columns}
            filterable={false}
            excludeFilterColumns={["actions"]}
            // excludeFilterColumns={excludeFilterColumns}
          />
        </div>
      )}

      {/* Modal for Adding or Editing */}
      <Modal
        isOpen={isModalOpen}
        onRequestClose={closeModal}
        title={editingComponentType ? "Edit Component Type" : "Add Component Type"}
      >
        <ComponentTypeForm
          loading={loading} 
          closeModal={closeModal}
          onSubmit={handleFormSubmit}
          initialValues={editingComponentType || { name: "", code: "", category: "", description: "", is_active: "", display_order: "" }}
          errors={formErrors}
        />
      </Modal>
    </div>
  );
});

export default SalaryComponentMaster;
